// chemistry-lite-element.ts
import { ASS } from "@adornis/ass/index.js";
import { noChange, render } from "lit";
import "./chemistry-lit-element.js";
import { DesignSystem } from "./elements/theming/design.js";
var registeredTokens = /* @__PURE__ */ new WeakMap();
var tokenStyleCache = /* @__PURE__ */ new Map();
var headStyle = {};
var ensureHeadStyleTag = (nodeName) => {
  if (headStyle[nodeName])
    return headStyle;
  headStyle[nodeName] = document.createElement("style");
  headStyle[nodeName].setAttribute(`liteelementstyle-${nodeName.toLowerCase()}`, "");
  window.document.head.appendChild(headStyle[nodeName]);
  return headStyle[nodeName];
};
var ChemistryLiteElement = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.renderTarget = null;
  }
  get spacing() {
    return DesignSystem.currentTheme.getValue()?.spacing;
  }
  get baseFontSize() {
    return DesignSystem.currentTheme.getValue()?.baseFontSize;
  }
  get baseFontSizePrint() {
    return DesignSystem.currentTheme.getValue()?.baseFontSizePrint;
  }
  get fontFamily() {
    return DesignSystem.currentTheme.getValue()?.fontFamily;
  }
  get fontFamilyHeading() {
    return DesignSystem.currentTheme.getValue()?.fontFamilyHeading || DesignSystem.currentTheme.getValue()?.fontFamily;
  }
  get colors() {
    return DesignSystem.currentTheme.getValue()?.colors;
  }
  get borders() {
    return DesignSystem.currentTheme.getValue()?.borders;
  }
  get sizes() {
    return DesignSystem.currentTheme.getValue()?.sizes;
  }
  get defaults() {
    return DesignSystem.currentTheme.getValue()?.defaults;
  }
  connectedCallback() {
    this.attachMyStyle();
    this.rerender();
  }
  disconnectedCallback() {
  }
  // doing custom optimization here, jssSheet.addRules is slower than this
  // even when properly cached, just appending a string is notably quicker than handling the CSSSheet
  attachMyStyle() {
    if (!this.isConnected)
      return;
    const elementName = this.nodeName.toLowerCase();
    if (!this.parentNode)
      return console.warn("no parent available yet, cant attach styles");
    const rootNodeHost = this.parentNode.getRootNode() === document ? document.body : this.parentNode.getRootNode().host;
    const keyElement = rootNodeHost || this.parentNode;
    if (!registeredTokens.has(keyElement))
      registeredTokens.set(keyElement, /* @__PURE__ */ new Set());
    if (registeredTokens.get(keyElement).has(elementName))
      return;
    registeredTokens.get(keyElement).add(elementName);
    const tokenStyleFn = tokenStyleCache.get(elementName) || (() => {
      let styles = this.styles?.();
      if (styles && !Array.isArray(styles))
        styles = [styles];
      return [
        { [elementName]: { boxSizing: "border-box", display: "block" } },
        ...styles ? styles.map((style) => ({
          [elementName]: style
        })) : []
      ];
    }).bind(this);
    if (!tokenStyleCache.has(elementName))
      tokenStyleCache.set(elementName, tokenStyleFn);
    const inWebcomponent = this.parentNode?.getRootNode() !== window.document;
    if (inWebcomponent && !rootNodeHost)
      return console.warn("this is not expected, fix me:", elementName);
    if (inWebcomponent) {
      rootNodeHost.addLiteElementStyleSheet(tokenStyleFn);
    } else {
      let styleTarget = ensureHeadStyleTag(this.nodeName);
      DesignSystem.currentTheme.subscribe((theme) => {
        const tokenStyle = tokenStyleFn();
        styleTarget.textContent = `${(Array.isArray(tokenStyle) ? tokenStyle : [tokenStyle]).map((style) => ASS.makeSheet(style).toString()).join("\n")}`;
      });
    }
  }
  render() {
    return noChange;
  }
  rerender() {
    const renderResult = this.render();
    if (renderResult === noChange)
      return;
    render(renderResult, this.renderTarget || this, {});
  }
};
export {
  ChemistryLiteElement
};
